import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CheckCircle2, MailWarning, Copy } from 'lucide-react';
import { PageHeader, Field, Spinner, FileInput, PageLoader } from '../../components/ui';
import api, { errMsg } from '../../services/api';
import { useAdmin, useGroup } from '../../context/AuthContext';

function GroupSettings() {
  const { setGroup } = useGroup();
  const [form, setForm] = useState(null);
  const [logo, setLogo] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then(r => {
      const g = r.data;
      setForm({ name: g.name, shortName: g.shortName || '', primaryColor: g.primaryColor, accentColor: g.accentColor, footerText: g.footerText || '', classes: g.classes.join(', ') });
      setLogoUrl(g.logoUrl);
    });
  }, []);

  if (!form) return <PageLoader />;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      let { data } = await api.put('/admin/settings', form);
      if (logo) {
        const fd = new FormData();
        fd.append('logo', logo);
        data = (await api.post('/admin/settings/logo', fd)).data;
        setLogo(null);
        setLogoUrl(data.logoUrl);
      }
      setGroup(data);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="card space-y-4 p-5">
      <h2 className="font-semibold text-navy-900">Group &amp; branding</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Group name" required><input className="input" value={form.name} onChange={e => set('name', e.target.value)} /></Field>
        <Field label="Short name" hint="Used as the email sender name"><input className="input" value={form.shortName} onChange={e => set('shortName', e.target.value)} /></Field>
        <Field label="Primary colour (navy)">
          <div className="flex gap-2"><input type="color" value={form.primaryColor} onChange={e => set('primaryColor', e.target.value)} className="h-9 w-12 rounded border" /><input className="input font-mono" value={form.primaryColor} onChange={e => set('primaryColor', e.target.value)} /></div>
        </Field>
        <Field label="Accent colour (gold)">
          <div className="flex gap-2"><input type="color" value={form.accentColor} onChange={e => set('accentColor', e.target.value)} className="h-9 w-12 rounded border" /><input className="input font-mono" value={form.accentColor} onChange={e => set('accentColor', e.target.value)} /></div>
        </Field>
      </div>
      <Field label="Logo" hint="Shown on the website and in email headers. PNG with a transparent or navy background works best.">
        <div className="flex items-center gap-4">
          {(logo || logoUrl) && <img src={logo ? URL.createObjectURL(logo) : logoUrl} alt="Logo" className="h-16 w-16 rounded-lg bg-navy-900 object-contain p-1" />}
          <FileInput accept="image/*" file={logo} onChange={setLogo} label="Change logo" />
        </div>
      </Field>
      <Field label="Classes" hint="Comma-separated. Shown as a dropdown on the registration form. Leave blank to let members type their class.">
        <input className="input" value={form.classes} onChange={e => set('classes', e.target.value)} placeholder="e.g. A, B, C, D, E, F" />
      </Field>
      <Field label="Default email footer"><textarea className="input" rows={3} value={form.footerText} onChange={e => set('footerText', e.target.value)} /></Field>
      <button disabled={busy} className="btn-primary">{busy && <Spinner size={16} />}Save settings</button>
    </form>
  );
}

function MyAccount() {
  const { admin, setAdmin } = useAdmin();
  const [profile, setProfile] = useState({ name: admin.name, email: admin.email });
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '' });
  const [busy, setBusy] = useState('');

  const saveProfile = async e => {
    e.preventDefault();
    setBusy('p');
    try { const { data } = await api.put('/admin/me', profile); setAdmin(data); toast.success('Saved'); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(''); }
  };
  const savePw = async e => {
    e.preventDefault();
    setBusy('pw');
    try { await api.put('/admin/me/password', pw); setPw({ currentPassword: '', newPassword: '' }); toast.success('Password changed'); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(''); }
  };

  return (
    <div className="card space-y-6 p-5">
      <form onSubmit={saveProfile} className="space-y-3">
        <h2 className="font-semibold text-navy-900">My account</h2>
        <Field label="Name"><input className="input" value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} /></Field>
        <Field label="Email"><input type="email" className="input" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} /></Field>
        <button disabled={busy === 'p'} className="btn-outline btn-sm">{busy === 'p' && <Spinner size={14} />}Save</button>
      </form>
      <form onSubmit={savePw} className="space-y-3 border-t pt-5">
        <h3 className="text-sm font-semibold text-navy-900">Change password</h3>
        <Field label="Current password"><input type="password" className="input" required value={pw.currentPassword} onChange={e => setPw(p => ({ ...p, currentPassword: e.target.value }))} /></Field>
        <Field label="New password"><input type="password" className="input" required minLength={8} value={pw.newPassword} onChange={e => setPw(p => ({ ...p, newPassword: e.target.value }))} /></Field>
        <button disabled={busy === 'pw'} className="btn-outline btn-sm">{busy === 'pw' && <Spinner size={14} />}Change password</button>
      </form>
    </div>
  );
}

function EmailStatus() {
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get('/admin/email-status').then(r => setStatus(r.data)).catch(() => {}); }, []);
  const link = `${window.location.origin}/register`;
  return (
    <div className="card space-y-4 p-5">
      <h2 className="font-semibold text-navy-900">Email sending</h2>
      {!status ? <Spinner className="text-gray-300" /> : status.ok
        ? <p className="flex items-center gap-2 text-sm text-green-700"><CheckCircle2 size={18} />Connected to the email service (Brevo)</p>
        : <p className="flex items-start gap-2 text-sm text-amber-800"><MailWarning size={18} className="flex-shrink-0" />{status.reason}</p>}
      <div className="border-t pt-4">
        <p className="label">Registration link for WhatsApp</p>
        <div className="flex gap-2">
          <input className="input font-mono text-xs" readOnly value={link} />
          <button className="btn-outline" onClick={() => { navigator.clipboard.writeText(link); toast.success('Copied'); }}><Copy size={16} /></button>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  return (
    <div>
      <PageHeader title="Settings" />
      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <GroupSettings />
        <div className="space-y-6">
          <EmailStatus />
          <MyAccount />
        </div>
      </div>
    </div>
  );
}
